import {mapObject} from '@benchling/js-utils';
import React from 'react';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {pluralize} from 'BenchlingUtil';
import {EventConstants} from 'constants/EventConstants';
import FileStates from 'FileStates';
import AppDispatcher from 'lib/Dispatcher';
import InvalidPartsModal from 'molbio/parts/InvalidPartsModal';
import {filesFetchCompleted} from 'redux-stores/ModelActions';
import {getStore} from 'redux-stores/Store';
import * as AutoFillFieldDAO from 'registry/AutoFillFieldDAO';
import {AutoFillFeatureResponse, AutoFillPartsResponse} from 'registry/AutoFillFieldDAO';
import * as TrayTaskActions from 'tray/TrayTaskActions';
import * as TrayTaskConstants from 'tray/TrayTaskConstants';
import runModal from 'ui-library/runModal';
import * as TaskRunner from 'util/TaskRunner';

async function updateSequences(
  response: AutoFillPartsResponse | AutoFillFeatureResponse,
  sequenceIds: Array<string>
): Promise<void> {
  // We need to submit FILE_TAGS_UPDATED to update the metadata tab. In the future, the metadata tab should
  // use the regular store/cache to access tag values, which would make this line unnecessary.
  AppDispatcher.dispatch({
    actionType: EventConstants.FILE_TAGS_UPDATED,
    tagData: Object.entries(response).map(([sequenceId, sequenceResponse]) => ({
      id: sequenceId,
      tags: sequenceResponse.sequenceSummary.tags,
    })),
  });
  getStore().dispatch(filesFetchCompleted(mapObject(response, (r) => r.sequenceSummary)));
  await FileStates.get().updateSequenceVersions(sequenceIds, {showHistoryPanel: false});
}

type AutoFillFeatureName = 'part' | 'transcription' | 'translation';

function getAutoFillActionFromName(
  featureName: AutoFillFeatureName
): (sequenceIds: Array<string>) => Promise<AutoFillPartsResponse | AutoFillFeatureResponse> {
  const autoFillFeatureNameToAction: {
    [featureName in AutoFillFeatureName]: (
      sequenceIds: Array<string>
    ) => Promise<AutoFillPartsResponse | AutoFillFeatureResponse>;
  } = {
    part: AutoFillFieldDAO.autofillParts,
    transcription: AutoFillFieldDAO.autofillTranscriptions,
    translation: AutoFillFieldDAO.autofillTranslations,
  };
  return autoFillFeatureNameToAction[featureName];
}

interface AutoFillProps {
  autoFillFeatureName: AutoFillFeatureName;
  customTaskHeading?: string;
  customSuccessMessage?: string;
}

async function maybeUnlinkInvalidParts(
  sequenceId: string,
  sequenceResponse: AutoFillFieldDAO.AutoFillPartsSequenceResponse
): Promise<void> {
  if (sequenceResponse.invalidPartSequenceIds.length > 0) {
    assert(BenchlingConfig.MOLBIO_ENABLE_AUTOFILL_CONFIGURABILITY, 'AutoFill config is not enabled');
    const shouldUnlink = await runModal(InvalidPartsModal, {
      invalidPartSequenceIds: sequenceResponse.invalidPartSequenceIds,
    });

    if (shouldUnlink) {
      const unlinkTaskId = TrayTaskActions.createTrayTask({
        heading: 'Unlinking invalid parts',
        progress: TrayTaskConstants.INDETERMINATE_PROGRESS,
      });

      try {
        await TaskRunner.postJSONPromise({
          url: '/1/api/unlink-parts',
          data: {
            unlinkSpecs: [
              {
                sequenceId,
                partSequenceIds: sequenceResponse.invalidPartSequenceIds,
              },
            ],
          },
        });

        TrayTaskActions.updateTrayTask(unlinkTaskId, {
          progress: TrayTaskConstants.SUCCESS_PROGRESS,
          heading:
            'Successfully unlinked invalid parts. We recommend that you also autofill again in order to keep the schema field values in sync.',
        });
      } catch (e) {
        TrayTaskActions.removeTrayTask(unlinkTaskId);
        throw e;
      }
    }
  }
}

interface AutoFillBioentityLinks extends AutoFillProps {
  sequenceId: string;
}

async function autoFillBioentityLinks({
  sequenceId,
  autoFillFeatureName,
  customTaskHeading,
  customSuccessMessage,
}: AutoFillBioentityLinks): Promise<void> {
  const pluralFeatureName = pluralize(2, autoFillFeatureName);
  const taskId = TrayTaskActions.createTrayTask({
    heading: customTaskHeading || `Autofilling ${pluralFeatureName}`,
    progress: TrayTaskConstants.INDETERMINATE_PROGRESS,
  });
  try {
    const response = await getAutoFillActionFromName(autoFillFeatureName)([sequenceId]);
    await updateSequences(response, [sequenceId]);
    let header;
    if (response[sequenceId].didModifySequence) {
      header = customSuccessMessage || `Successfully ran autofill ${pluralFeatureName}.`;
    } else {
      header = `No new ${pluralFeatureName} were found.`;
    }
    let subheading;
    const {warningMessages} = response[sequenceId];
    if (warningMessages && warningMessages.length > 0) {
      subheading = (
        <>
          <p>**Warnings:**</p>
          {warningMessages.map((warning) => (
            <p key={warning}>{warning}</p>
          ))}
        </>
      );
    }
    TrayTaskActions.updateTrayTask(taskId, {
      progress: TrayTaskConstants.SUCCESS_PROGRESS,
      heading: (
        <>
          <p>{header}</p>
          {subheading}
        </>
      ),
    });
    if (AutoFillFieldDAO.isAutoFillPartsSequenceResponse(response[sequenceId])) {
      await maybeUnlinkInvalidParts(sequenceId, response[sequenceId]);
    }
  } catch (e) {
    // Our top-level handler will show an appropriate error message, so just hide the current tray task.
    TrayTaskActions.removeTrayTask(taskId);
    throw e;
  }
}

interface BulkAutoFillBioentityLinks extends AutoFillProps {
  sequenceIds: Array<string>;
}

async function bulkAutoFillBioentityLinks({
  sequenceIds,
  autoFillFeatureName,
  customTaskHeading,
  customSuccessMessage,
}: BulkAutoFillBioentityLinks): Promise<void> {
  const pluralFeatureName = pluralize(2, autoFillFeatureName);
  const length = sequenceIds.length;
  const taskId = TrayTaskActions.createTrayTask({
    heading:
      customTaskHeading ||
      `Autofilling ${pluralFeatureName} on ${length} ${pluralize(length, 'DNA sequence')}`,
    progress: TrayTaskConstants.INDETERMINATE_PROGRESS,
  });
  try {
    const response = await getAutoFillActionFromName(autoFillFeatureName)(sequenceIds);
    await updateSequences(response, sequenceIds);
    TrayTaskActions.updateTrayTask(taskId, {
      progress: TrayTaskConstants.SUCCESS_PROGRESS,
      heading:
        customSuccessMessage ||
        `Successfully ran autofill ${pluralFeatureName} on ${length} ${pluralize(length, 'sequence')}.`,
    });
  } catch (e) {
    // Our top-level handler will show an appropriate error message, so just hide the current tray task.
    TrayTaskActions.removeTrayTask(taskId);
    throw e;
  }
}

export async function autoFillPartFields(sequenceId: string): Promise<void> {
  await autoFillBioentityLinks({
    sequenceId,
    autoFillFeatureName: 'part',
    customTaskHeading: 'Autofilling part fields',
  });
}

export async function bulkAutoFillPartFields(sequenceIds: Array<string>): Promise<void> {
  const length = sequenceIds.length;
  const customTaskHeading = `Autofilling part fields on ${length} ${pluralize(length, 'DNA sequence')}`;
  await bulkAutoFillBioentityLinks({
    sequenceIds,
    autoFillFeatureName: 'part',
    customTaskHeading,
  });
}

export async function autofillTranslations(sequenceId: string): Promise<void> {
  await autoFillBioentityLinks({
    sequenceId,
    autoFillFeatureName: 'translation',
  });
}

export async function bulkAutofillTranslations(sequenceIds: Array<string>): Promise<void> {
  await bulkAutoFillBioentityLinks({sequenceIds, autoFillFeatureName: 'translation'});
}

export async function autofillTranscriptions(sequenceId: string): Promise<void> {
  await autoFillBioentityLinks({sequenceId, autoFillFeatureName: 'transcription'});
}

export async function bulkAutofillTranscriptions(sequenceIds: Array<string>): Promise<void> {
  await bulkAutoFillBioentityLinks({sequenceIds, autoFillFeatureName: 'transcription'});
}
